import React from 'react';
import Family from '../assets/father-and-boys.png';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import { IPersonalInfo } from '../types/get-started.type';
import { isLeadWayEmail } from 'utils';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

type IProps = {
  onSubmit: (formValue: IPersonalInfo) => void;
};

export default function PersonalInfo({ onSubmit }: IProps) {
  const formik = useFormik({
    initialValues: {
      surname: '',
      otherName: '',
      email: '',
      phoneNumber: ''
    },
    validationSchema: Yup.object({
      surname: Yup.string().required('Surname is required').nullable(),
      otherName: Yup.string().required('Other Names is required').nullable(),
      email: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .nullable()
        .test(
          'leadway-is-allowed',
          'Email is not allow or already in use',
          async function (value) {
            if (value) {
              if (isLeadWayEmail(value)) {
                return false;
              }
              return true;
            }
            return true;
          }
        ),
      phoneNumber: Yup.string()
        .min(7, 'Phone number must be at least 7 digits')
        .required('Phone Number is required')
        .nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSubmit(values);
    }
  });

  return (
    <div className="flex flex-col-reverse md:flex-row">
      <div className="mx-20 mt-44 md:w-1/2">
        <p className="text-3xl font-medium text-[#353535]">
          Let&apos;s Get To Know You
        </p>
        <p className="text-sm font-bold text-primary-900">
          Please provide the information below:
        </p>

        <form
          autoComplete="false"
          className="mt-10 space-y-3"
          onSubmit={formik.handleSubmit}
        >
          <div className="grid md:grid-cols-2 md:gap-6">
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="surname"
                placeholder=""
                value={formik.values.surname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Surname"
                error={formik.touched.surname ? formik.errors.surname : ''}
                isRequired
                size="lg"
                maxLength={50}
                className="rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="otherName"
                placeholder=""
                value={formik.values.otherName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Other Names"
                error={formik.touched.otherName ? formik.errors.otherName : ''}
                isRequired
                size="lg"
                maxLength={100}
                className="rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 md:gap-6">
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="email"
                placeholder=""
                value={formik.values.email}
                onChange={e => {
                  formik.handleChange(e);
                  formik.validateField('email');
                }}
                onBlur={formik.handleBlur}
                label="Email"
                error={formik.touched.email ? formik.errors.email : ''}
                isRequired
                size="lg"
                maxLength={55}
                className="rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
            <div className="group relative z-0 mb-6 w-full">
              <PhoneInput
                defaultCountry="NG"
                placeholder="Enter phone number"
                withCountryCallingCode
                name="phoneNumber"
                international
                value={formik.values.phoneNumber}
                onChange={val => formik.setFieldValue('phoneNumber', val)}
                error={
                  formik.touched.phoneNumber && formik.values.phoneNumber
                    ? isValidPhoneNumber(formik.values.phoneNumber)
                      ? undefined
                      : 'Invalid phone number'
                    : 'Phone number required'
                }
                className="mt-6 rounded-2xl border-[1.5px] border-gray-300 p-1 px-2"
                onBlur={formik.handleBlur}
                limitMaxLength
                maxLength={25}
              />
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            className="mt-5 w-[7rem] rounded p-[0.1rem]"
            label="Proceed"
            type="submit"
            disabled={Object.keys(formik.errors).length > 0}
          ></Button>
        </form>
      </div>
      <div className="md:w-1/2">
        <img
          src={Family}
          className="hidden h-auto w-full object-cover md:flex md:h-screen md:flex-col"
          alt="Graduate"
        />
      </div>
    </div>
  );
}
